import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "@phosphor-icons/react";

const KEY = "mh_site_access_ok_v1";

export default function SiteGate({ children }) {
  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [passed, setPassed] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/site/gate-enabled");
        if (cancelled) return;
        setEnabled(data.enabled);
        if (!data.enabled) {
          setPassed(true);
        } else if (localStorage.getItem(KEY) === "1") {
          setPassed(true);
        }
      } catch {
        // If gate check fails, don't block the site
        setPassed(true);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/site/verify-access", { code: code.trim() });
      localStorage.setItem(KEY, "1");
      setPassed(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setLoading(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="font-display text-4xl uppercase text-white/40 animate-pulse">MERCH/HUB</div>
      </div>
    );
  }

  if (!enabled || passed) return children;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6 relative overflow-hidden" data-testid="site-gate">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,59,0,0.15),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(255,59,0,0.1),transparent_50%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <div className="font-display text-6xl md:text-8xl uppercase leading-none">MERCH<span className="text-[#FF3B00]">/</span>HUB</div>
          <div className="mt-3 text-xs uppercase tracking-[0.4em] text-white/40">Accès restreint · Bêta privée</div>
        </div>

        <form onSubmit={submit} className="border border-white/20 bg-black/60 backdrop-blur-md p-8 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FF3B00] flex items-center gap-2">
              <Lock size={14} weight="bold" /> Code d'accès requis
            </div>
            <p className="mt-3 text-sm text-white/70">
              Ce site est en accès restreint. Entrez le code que vous avez reçu pour continuer.
            </p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Code d'accès</label>
            <input
              data-testid="gate-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              autoCapitalize="characters"
              placeholder="XXXXXXXX"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-4 text-lg tracking-widest font-mono uppercase"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !code.trim()}
            data-testid="gate-submit"
            className="w-full py-4 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
            {loading ? "Vérification..." : "Entrer"}
          </button>
          <div className="text-xs text-white/40 text-center pt-2 border-t border-white/10">
            Vous voulez un accès ? Contactez <a href="mailto:merchhub8@gmail.com" className="text-[#FF3B00] hover:underline">merchhub8@gmail.com</a>
          </div>
        </form>
      </div>
    </div>
  );
}
